import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { ApiError, api, setUnauthorizedHandler } from "../api/client";
import type { AdminUser, Role } from "../api/types";

/**
 * Session state, mirrored from the server.
 *
 * There is nothing persisted here: the cookie is the session, and this context is
 * only a cache of "who does the server think I am". On a hard refresh the answer
 * comes from `/api/auth/me`, so the client cannot believe in a session the server
 * has already revoked.
 */

const ROLE_RANK: Record<Role, number> = { viewer: 0, operator: 1, owner: 2 };

interface AuthState {
  /** `undefined` while the initial /me is in flight, `null` when signed out. */
  user: AdminUser | null | undefined;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** True when the signed-in admin's role is at least `required`. */
  can: (required: Role) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null | undefined>(undefined);

  useEffect(() => {
    setUnauthorizedHandler(() => setUser(null));
    return () => setUnauthorizedHandler(null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ user: AdminUser }>("/api/auth/me")
      .then((res) => {
        if (!cancelled) setUser(res.user);
      })
      .catch(() => {
        if (!cancelled) setUser(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const res = await api.post<{ user: AdminUser }>("/api/auth/login", { email, password });
    setUser(res.user);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.post("/api/auth/logout");
    } catch (err) {
      // A failed logout still ends the local session; the cookie expires on its
      // own and leaving the operator staring at a console they wanted to leave is
      // the worse outcome.
      if (!(err instanceof ApiError)) throw err;
    }
    setUser(null);
  }, []);

  const can = useCallback(
    (required: Role) => (user ? ROLE_RANK[user.role] >= ROLE_RANK[required] : false),
    [user],
  );

  const value = useMemo<AuthState>(() => ({ user, signIn, signOut, can }), [user, signIn, signOut, can]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
